import { useCallback, useEffect, useRef, useState } from "react";
import api from "../../services/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Loader2, MapPin, Navigation, Package, Calculator } from "lucide-react";
import { toast } from "sonner";

/**
 * Admin-only zone preview simulator.
 *
 * Calls the SAME /api/pricing/estimate endpoint that customers use, so there is
 * no duplicated pricing logic on the frontend. Pure read-only quote — does NOT
 * create orders, does NOT mutate config, does NOT touch Razorpay/COD/OTP flows.
 *
 * Re-runs automatically when `refreshKey` changes (admin clicked Save on the
 * pricing config above), so the simulator stays in sync with the live engine.
 */
const ZONE_COLORS = {
  A: "bg-emerald-50 text-emerald-700 border-emerald-200 ring-emerald-100",
  B: "bg-blue-50 text-blue-700 border-blue-200 ring-blue-100",
  C: "bg-amber-50 text-amber-700 border-amber-200 ring-amber-100",
  D: "bg-red-50 text-red-700 border-red-200 ring-red-100",
};

const SAMPLE_WEIGHTS = [1, 5, 20];

export default function ZonePreviewSimulator({ refreshKey = 0 }) {
  const [pickup, setPickup] = useState("Koramangala, Bangalore");
  const [delivery, setDelivery] = useState("Connaught Place, Delhi");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [quotes, setQuotes] = useState(null); // { distance_km, zone, zone_label, zone_multiplier, rows: [{weight, final_price, service_charges, gst, slab, cod_fee}] }

  const lastAddressesRef = useRef({ pickup: "", delivery: "" });

  const runSimulation = useCallback(async ({ silent = false } = {}) => {
    if (!pickup.trim() || !delivery.trim()) {
      if (!silent) toast.error("Enter both pickup and delivery addresses");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const results = await Promise.all(
        SAMPLE_WEIGHTS.map((w) =>
          api.get("/pricing/estimate", {
            params: {
              weight: w,
              pickup: pickup.trim(),
              delivery: delivery.trim(),
              item_size: "medium",
            },
          })
        )
      );
      const first = results[0].data;
      const rows = results.map((r, i) => ({
        weight: SAMPLE_WEIGHTS[i],
        final_price: r.data.final_price ?? r.data.total ?? 0,
        service_charges: r.data.service_charges ?? 0,
        gst: r.data.gst ?? 0,
        slab: r.data.weight_slab || `${SAMPLE_WEIGHTS[i]} kg`,
        weight_charge: r.data.weight_charge_display ?? r.data.weight_charge ?? 0,
        slab_raw: r.data.slab_charge_raw ?? 0,
        zone_uplift: r.data.zone_uplift ?? 0,
        longhaul: r.data.longhaul_surcharge ?? 0,
      }));
      setQuotes({
        distance_km: first.distance_km ?? first.road_distance_km ?? 0,
        distance_source: first.distance_source || "haversine",
        zone: first.zone,
        zone_label: first.zone_label,
        zone_multiplier: first.zone_multiplier,
        rows,
      });
      lastAddressesRef.current = { pickup: pickup.trim(), delivery: delivery.trim() };
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Failed to resolve route";
      setError(msg);
      setQuotes(null);
      if (!silent) toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, [pickup, delivery]);

  // Auto re-run when admin saves pricing config (refreshKey bump) — only if
  // the current addresses match what was last simulated (i.e., the user has a
  // valid resolved quote on screen). Otherwise, stay quiet.
  useEffect(() => {
    if (refreshKey === 0) return;
    const last = lastAddressesRef.current;
    if (last.pickup && last.delivery && last.pickup === pickup.trim() && last.delivery === delivery.trim()) {
      runSimulation({ silent: true });
    }
  }, [refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const zoneColor = quotes?.zone ? ZONE_COLORS[quotes.zone] || ZONE_COLORS.A : "";

  return (
    <div
      className="bg-white rounded-2xl border border-slate-200 p-5"
      data-testid="zone-preview-simulator"
    >
      <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-xl bg-purple-50 flex items-center justify-center">
            <Calculator className="h-5 w-5 text-purple-600" />
          </div>
          <div>
            <h2 className="font-['Outfit'] font-bold text-slate-900">Zone Preview Simulator</h2>
            <p className="text-xs text-slate-500">
              Sanity-check real customer quotes for any pickup → delivery pair. Uses the live pricing engine — no orders created.
            </p>
          </div>
        </div>
        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold bg-slate-900 text-white">
          <Package className="h-3 w-3" /> PREVIEW ONLY
        </span>
      </div>

      {/* Address inputs */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
        <div>
          <Label className="text-[11px] uppercase font-semibold text-slate-500 flex items-center gap-1">
            <MapPin className="h-3 w-3 text-emerald-500" /> Pickup Address
          </Label>
          <Input
            value={pickup}
            onChange={(e) => setPickup(e.target.value)}
            placeholder="e.g. MG Road, Bangalore 560001"
            className="mt-1 rounded-lg h-9 text-sm"
            data-testid="sim-pickup-input"
          />
        </div>
        <div>
          <Label className="text-[11px] uppercase font-semibold text-slate-500 flex items-center gap-1">
            <Navigation className="h-3 w-3 text-[#FF6B00]" /> Delivery Address
          </Label>
          <Input
            value={delivery}
            onChange={(e) => setDelivery(e.target.value)}
            placeholder="e.g. Connaught Place, Delhi 110001"
            className="mt-1 rounded-lg h-9 text-sm"
            data-testid="sim-delivery-input"
          />
        </div>
      </div>

      <Button
        onClick={() => runSimulation()}
        disabled={loading}
        className="rounded-xl h-9 gap-1.5 bg-purple-600 hover:bg-purple-700 text-white"
        data-testid="sim-calculate-btn"
      >
        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Calculator className="h-4 w-4" />}
        {loading ? "Calculating…" : "Calculate"}
      </Button>

      {error && !loading && (
        <div
          className="mt-3 px-3 py-2 rounded-lg bg-red-50 border border-red-200 text-red-700 text-xs"
          data-testid="sim-error"
        >
          {error}
        </div>
      )}

      {quotes && !loading && (
        <div className="mt-4 space-y-3" data-testid="sim-results">
          {/* Route summary */}
          <div className="rounded-xl bg-slate-50 border border-slate-100 p-3 grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
            <div>
              <div className="text-[10px] uppercase font-semibold text-slate-400">Distance</div>
              <div className="mt-0.5 font-bold text-slate-900 text-sm" data-testid="sim-distance">
                {quotes.distance_km} km
                <span className="ml-1 text-[9px] font-semibold text-slate-400 uppercase">
                  {quotes.distance_source === "osrm" ? "road" : "est"}
                </span>
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase font-semibold text-slate-400">Detected Zone</div>
              <div className="mt-0.5">
                <span
                  className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-lg border text-xs font-bold ring-2 ${zoneColor}`}
                  data-testid="sim-zone-badge"
                >
                  <MapPin className="h-3 w-3" />
                  Zone {quotes.zone}
                </span>
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase font-semibold text-slate-400">Zone Label</div>
              <div className="mt-0.5 font-bold text-slate-900 text-sm" data-testid="sim-zone-label">
                {quotes.zone_label || "—"}
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase font-semibold text-slate-400">Multiplier</div>
              <div className="mt-0.5 font-bold text-slate-900 text-sm" data-testid="sim-zone-multiplier">
                ×{Number(quotes.zone_multiplier ?? 1).toFixed(2)}
              </div>
            </div>
          </div>

          {/* 3-weight grid */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {quotes.rows.map((row) => (
              <div
                key={row.weight}
                className="rounded-xl border border-slate-200 p-3 hover:border-purple-200 hover:shadow-sm transition-all"
                data-testid={`sim-row-${row.weight}kg`}
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-purple-50 text-purple-700 text-[11px] font-bold">
                    <Package className="h-3 w-3" /> {row.weight} kg
                  </span>
                  <span className="text-[10px] text-slate-400 truncate max-w-[60%] text-right" title={row.slab}>
                    {row.slab}
                  </span>
                </div>
                <div className="space-y-0.5 text-[11px] text-slate-500">
                  <div className="flex justify-between">
                    <span>Weight (slab × zone)</span>
                    <span className="font-medium text-slate-700">INR {row.weight_charge}</span>
                  </div>
                  {row.zone_uplift > 0 && (
                    <div className="flex justify-between text-blue-600">
                      <span className="pl-2">↳ zone uplift</span>
                      <span className="font-medium">+INR {row.zone_uplift}</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span>Service charges</span>
                    <span className="font-medium text-slate-700">INR {row.service_charges - row.weight_charge - row.longhaul}</span>
                  </div>
                  {row.longhaul > 0 && (
                    <div className="flex justify-between text-amber-600">
                      <span>Long-haul surcharge</span>
                      <span className="font-medium">INR {row.longhaul}</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span>GST 18%</span>
                    <span className="font-medium text-slate-700">INR {row.gst}</span>
                  </div>
                </div>
                <div className="mt-2 pt-2 border-t border-slate-100 flex justify-between items-center">
                  <span className="font-bold text-[11px] text-slate-600 uppercase">Total</span>
                  <span
                    className="font-extrabold text-[#FF6B00] text-base"
                    data-testid={`sim-total-${row.weight}kg`}
                  >
                    INR {row.final_price}
                  </span>
                </div>
              </div>
            ))}
          </div>

          <p className="text-[10px] text-slate-400 italic">
            Prices above are computed by the exact same <code>/api/pricing/estimate</code> endpoint served to customers. No orders are created, no config is modified.
          </p>
        </div>
      )}
    </div>
  );
}
